import { useMemo } from "react";
import dayjs from "dayjs";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import {
  buildEncounterDateMap,
  groupDocumentReferenceChains,
  pickLatestDocumentEntry,
  resolveChainDates,
  type DocumentChainDates,
} from "./documentReissueChain";

/**
 * Processamento centralizado das listas de documentos assinados/pendentes.
 * Extraído de Documents.tsx para reutilizar no atalho de reemissão (PatientSearch)
 * sem duplicar filtros nem mudar o comportamento do accordion no EHR.
 *
 * Também agrupa versões ligadas por relatesTo/replaces (remissões) em uma linha
 * por documento lógico, com primeira emissão / última remissão / histórico.
 */

function cleanAndJoin(expressions: any, delimiter = ", ") {
  return Array.from(
    new Set(expressions.filter((item: any) => item && item.trim() !== "")),
  ).join(delimiter);
}

function translateCategory(category: any) {
  if (category === "Laboratory procedure") {
    return "Exame Laboratorial";
  } else if (category === "Imaging") {
    return "Exame de imagem";
  } else {
    return category;
  }
}

/** Compara o ID do practitioner do documento com o userPractitioner logado. */
function matchesPractitioner(
  documentPractitionerId: string | undefined,
  userPractitionerId: string | undefined,
): boolean {
  if (!documentPractitionerId || !userPractitionerId) return false;

  const docId = documentPractitionerId.includes("-")
    ? documentPractitionerId.split("-")[1]
    : documentPractitionerId;
  const userId = userPractitionerId.includes("-")
    ? userPractitionerId.split("-")[1]
    : userPractitionerId;

  return docId === userId || documentPractitionerId === userPractitionerId;
}

function applyChainDates(
  row: Record<string, any>,
  chainDates: DocumentChainDates,
) {
  return {
    ...row,
    // `date` permanece = primeira emissão (compatível com cards/carousel existentes)
    date: chainDates.firstDate,
    firstDate: chainDates.firstDate,
    lastDate: chainDates.lastDate,
    reissueCount: chainDates.reissueCount,
    hasReissueHistory: chainDates.hasReissueHistory,
    history: chainDates.history,
  };
}

export function processMedicalNoteData(
  medicalNoteData: any[] | undefined,
  sign: boolean,
  userPractitionerRoleId?: string,
  encounterEntries?: any[],
) {
  const encounterDateMap = buildEncounterDateMap(encounterEntries);

  const documentReference = medicalNoteData
    ?.filter((el: any) => el?.resource?.resourceType === "DocumentReference")
    ?.filter((e: any) => {
      return sign
        ? e?.resource?.securityLabel?.[0]?.coding?.[0]?.code === "ProofOfOrigin"
        : e?.resource?.securityLabel?.[0]?.coding?.[0]?.code !==
            "ProofOfOrigin";
    });

  const medicationRequest = medicalNoteData?.filter(
    (el: any) =>
      el?.resource?.resourceType === "MedicationRequest" &&
      (sign
        ? true
        : el?.resource?.requester?.reference?.split("/")[1] ===
          userPractitionerRoleId),
  );

  const chains = groupDocumentReferenceChains(documentReference || []);

  const rowData =
    chains
      .map((chain, index) => {
        const el = pickLatestDocumentEntry(chain);
        if (!el) return null;

        const chainDates = resolveChainDates(chain, encounterDateMap);

        const auxPractitioner = el?.resource?.contained?.find(
          (contained: any) => contained?.resourceType === "Practitioner",
        );

        const auxSpecialty =
          el?.resource?.practiceSetting?.coding?.[0]?.display;

        // basedOn pode vir como objeto único (XML→JSON) ou array
        const basedOnList = Array.isArray(el?.resource?.basedOn)
          ? el.resource.basedOn
          : el?.resource?.basedOn
            ? [el.resource.basedOn]
            : [];
        const auxDescription = medicationRequest?.filter((request: any) => {
          const findRef = basedOnList.find((basedOn: any) =>
            basedOn?.reference?.includes(request.resource.id),
          );
          return findRef;
        });

        const auxType = el?.resource?.type?.coding?.[0]?.display || "-";

        return applyChainDates(
          {
            id: index,
            sign,
            type: auxType,
            fullResource: el?.resource,
            fullMedicationResource: auxDescription,
            description:
              auxDescription
                ?.map(
                  (medicationRequestPrescription: any) =>
                    medicationRequestPrescription?.resource?.medication?.concept
                      ?.text,
                )
                ?.join(" , ") || "",
            date: dayjs(el?.resource?.date).format("DD/MM/YYYY") || "-",
            specialty: auxSpecialty || "-",
            practitioner:
              auxPractitioner?.name?.[0]?.given?.[0] ||
              auxPractitioner?.identifier?.[0]?.value,
            binaryUrl: el?.resource?.content?.[0]?.attachment?.url || "",
            medication: auxDescription,
          },
          chainDates,
        );
      })
      .filter(Boolean)
      ?.filter(
        (document: any) => document?.fullMedicationResource?.length > 0,
      ) || [];

  return rowData;
}

export function processSolicitationData(
  solicitationData: any[] | undefined,
  resultsData: any,
  sign: boolean,
  userPractitionerId?: string,
  encounterEntries?: any[],
) {
  const encounterDateMap = buildEncounterDateMap(encounterEntries);

  const documentReference = solicitationData
    ?.filter((el: any) => el?.resource?.resourceType === "DocumentReference")
    ?.filter((e: any) => {
      return sign
        ? e?.resource?.securityLabel?.[0]?.coding?.[0]?.code ===
            "ProofOfOrigin" &&
            e?.resource?.category?.[0]?.coding?.[0]?.display === "Solicitação"
        : e?.resource?.securityLabel?.[0]?.coding?.[0]?.code !==
            "ProofOfOrigin" &&
            e?.resource?.category?.[0]?.coding?.[0]?.display === "Solicitação";
    });

  const serviceRequest = solicitationData?.filter(
    (el: any) => el?.resource?.resourceType === "ServiceRequest",
  );

  const chains = groupDocumentReferenceChains(documentReference || []);

  const rowData =
    chains
      .map((chain, index) => {
        const el = pickLatestDocumentEntry(chain);
        if (!el) return null;

        const chainDates = resolveChainDates(chain, encounterDateMap);

        const auxPractitioner = el?.resource?.contained?.find(
          (contained: any) => contained?.resourceType === "Practitioner",
        );

        const auxSpecialty =
          el?.resource?.practiceSetting?.coding?.[0]?.display;

        const basedOnList = Array.isArray(el?.resource?.basedOn)
          ? el.resource.basedOn
          : el?.resource?.basedOn
            ? [el.resource.basedOn]
            : [];
        const auxDescription = serviceRequest?.filter((request: any) => {
          const findRef = basedOnList.find((basedOn: any) =>
            basedOn?.reference?.includes(request.resource.id),
          );
          return findRef;
        });

        const auxCategory = cleanAndJoin(
          auxDescription
            ?.filter(
              (item: any) =>
                item !== null && item !== undefined && item !== "",
            )
            ?.map(
              (el: any) => el?.resource?.category?.[0]?.coding?.[0].display,
            ),
        );

        const auxResults = resultsData?.[0]?.entry?.filter((result: any) => {
          const findRef = auxDescription?.find(
            (service: any) =>
              service?.resource?.id ===
              result?.resource?.basedOn
                ?.find((bas: any) => bas.reference.includes("ServiceRequest"))
                ?.reference?.split("/")?.[1],
          );
          return findRef;
        });

        const resultUrls =
          auxResults
            ?.map(
              (result: any) =>
                result?.resource?.content?.[0]?.attachment?.url || "",
            )
            .filter((url: string) => url !== "") || [];

        const resultUrl = resultUrls.length > 0 ? resultUrls[0] : "";

        const auxType = el?.resource?.type?.coding?.[0]?.display || "-";

        const practitionerId = auxPractitioner?.identifier?.[0]?.value;

        return applyChainDates(
          {
            id: index,
            sign,
            type: auxType,
            fullResource: el?.resource,
            fullServiceResource: auxDescription,
            category: translateCategory(auxCategory),
            description:
              `${auxDescription
                ?.map(
                  (serviceRequestItem: any) =>
                    serviceRequestItem?.resource?.code?.concept?.coding?.[0]
                      ?.display,
                )
                ?.join(", ")}` || "",
            date: dayjs(el?.resource?.date).format("DD/MM/YYYY") || "-",
            specialty: auxSpecialty || "-",
            practitioner:
              auxPractitioner?.name?.[0]?.given?.[0] ||
              auxPractitioner?.identifier?.[0]?.value,
            practitionerId: practitionerId,
            binaryUrl: el?.resource?.content?.[0]?.attachment?.url || "",
            auxResults,
            resultUrls,
            resultUrl,
          },
          chainDates,
        );
      })
      .filter(Boolean)
      ?.filter((document: any) => {
        if (sign) return true;
        return matchesPractitioner(document?.practitionerId, userPractitionerId);
      }) || [];

  return rowData;
}

export function processNoteReportData(
  NoteReportData: any[] | undefined,
  sign: boolean,
  userPractitionerId?: string,
  encounterEntries?: any[],
) {
  const encounterDateMap = buildEncounterDateMap(encounterEntries);

  const documentReference =
    NoteReportData?.filter((e: any) => {
      return sign
        ? e?.resource?.securityLabel?.[0]?.coding?.[0]?.code === "ProofOfOrigin"
        : e?.resource?.securityLabel?.[0]?.coding?.[0]?.code !==
            "ProofOfOrigin";
    }) || [];

  const chains = groupDocumentReferenceChains(documentReference);

  const rowData =
    chains
      .map((chain, index) => {
        const el = pickLatestDocumentEntry(chain);
        if (!el) return null;

        const chainDates = resolveChainDates(chain, encounterDateMap);

        const auxPractitioner = el?.resource?.contained?.find(
          (contained: any) => contained?.resourceType === "Practitioner",
        );

        const auxSpecialty =
          el?.resource?.practiceSetting?.coding?.[0]?.display;

        const practitionerId = auxPractitioner?.identifier?.[0]?.value;

        return applyChainDates(
          {
            id: index,
            fullResource: el?.resource,
            practitioner:
              auxPractitioner?.name?.[0]?.given?.[0] ||
              auxPractitioner?.identifier?.[0]?.value,
            practitionerId: practitionerId,
            date: dayjs(el?.resource?.date).format("DD/MM/YYYY") || "-",
            specialty: auxSpecialty || "-",
            sign,
            binaryUrl: el?.resource?.content?.[0]?.attachment?.url || "",
          },
          chainDates,
        );
      })
      .filter(Boolean)
      ?.filter((document: any) => {
        if (sign) return true;
        return matchesPractitioner(document?.practitionerId, userPractitionerId);
      }) || [];

  return rowData;
}

export type UseProcessedDocumentListsParams = {
  medicalNoteData?: any[];
  solicitationData?: any[];
  resultsData?: any;
  NoteReportData?: any[];
  sign?: boolean;
  /** Entries de Encounter do PatientSummary — usadas na data da primeira emissão. */
  encounterData?: any[];
};

/**
 * Hook usado por Documents (EHR) e pelo atalho de reemissão.
 * Mantém as mesmas dependências e regras do accordion original.
 */
export function useProcessedDocumentLists({
  medicalNoteData,
  solicitationData,
  resultsData,
  NoteReportData,
  sign = false,
  encounterData,
}: UseProcessedDocumentListsParams) {
  const { userPractitionerRole, userPractitioner } =
    usePractitionerRoleContext();

  const processedMedicalNoteData = useMemo(
    () =>
      processMedicalNoteData(
        medicalNoteData,
        sign,
        userPractitionerRole?.id,
        encounterData,
      ),
    [medicalNoteData, sign, userPractitionerRole?.id, encounterData],
  );

  const processedSolicitationData = useMemo(
    () =>
      processSolicitationData(
        solicitationData,
        resultsData,
        sign,
        userPractitioner?.id,
        encounterData,
      ),
    [solicitationData, resultsData, sign, userPractitioner?.id, encounterData],
  );

  const processedNoteReportData = useMemo(
    () =>
      processNoteReportData(
        NoteReportData,
        sign,
        userPractitioner?.id,
        encounterData,
      ),
    [NoteReportData, sign, userPractitioner?.id, encounterData],
  );

  return {
    processedMedicalNoteData,
    processedSolicitationData,
    processedNoteReportData,
    filteredMedicalNoteCount: processedMedicalNoteData?.length || 0,
    filteredSolicitationCount: processedSolicitationData?.length || 0,
    filteredNoteReportCount: processedNoteReportData?.length || 0,
  };
}
